import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";

interface SiteLayoutProps {
  children: React.ReactNode;
  navVariant?: "transparent" | "solid";
}

const seoMap: Record<
  string,
  {
    title: string;
    description: string;
  }
> = {
  "/": {
    title: "엠씨테크 MC TECH | 롤프레스·서보유압·MOOG 서보밸브",
    description:
      "엠씨테크(MC TECH)는 롤프레스 서보유압 시스템, MOOG 서보밸브, 모션컨트롤러, 유압실린더, 위치·압력센서 공급과 제어 설계 및 현장 시운전을 지원합니다.",
  },

  "/roll-press": {
    title: "롤프레스 서보유압 제어 시스템 | 엠씨테크 MC TECH",
    description:
      "롤프레스 장비의 정밀 압력·위치·갭 제어를 위한 서보유압 시스템 설계, 제작 및 현장 시운전을 지원합니다.",
  },

  "/servo-hydraulic": {
    title: "서보유압 시스템·MOOG 서보밸브 | 엠씨테크 MC TECH",
    description:
      "MOOG 서보밸브를 활용한 서보유압 시스템, 유압유니트, 실린더 제어 및 정밀 압력·위치 제어 솔루션을 제공합니다.",
  },

  "/servo-valve": {
  title: "서보밸브·Servo Valve·SERVOVALVE | MOOG | 엠씨테크",
  description:
    "엠씨테크는 MOOG 서보밸브(Servo Valve, SERVOVALVE) 공급과 유압·모션제어 시스템 설계, 위치·압력 제어 및 현장 시운전을 지원합니다.",
  },

  "/explosion-proof-servo-motor": {
  title: "방폭서보모터·Explosion Proof Servo Motor | 엠씨테크",
  description:
    "엠씨테크는 산업용 방폭서보모터(Explosion Proof Servo Motor) 공급과 Servo Drive, Motion Controller 연동, 정밀 위치·속도 제어 및 현장 시운전을 지원합니다.",
  },

  "/products": {
    title: "MOOG 서보밸브·모션컨트롤러·유압제품 | 엠씨테크",
    description:
      "MOOG 서보밸브, 모션컨트롤러, 서보드라이브, 유압실린더, 위치센서, 압력센서 등 산업용 유압·모션제어 제품을 제공합니다.",
  },

  "/engineering": {
    title: "유압·모션제어 엔지니어링 및 시운전 | 엠씨테크",
    description:
      "엠씨테크는 서보유압 및 모션제어 시스템 설계, 프로그램 설정, 현장 설치, 튜닝 및 시운전 서비스를 제공합니다.",
  },

  "/company": {
    title: "엠씨테크 회사소개 | 서보유압·모션제어 전문기업",
    description:
      "엠씨테크(MC TECH)는 산업용 서보유압, MOOG 서보밸브 및 정밀 모션제어 시스템을 전문으로 하는 엔지니어링 기업입니다.",
  },

  "/contact": {
    title: "견적문의 | 엠씨테크 MC TECH",
    description:
      "MOOG 서보밸브, 롤프레스 서보유압 시스템, 모션제어 제품 및 엔지니어링 관련 견적과 기술 문의를 접수합니다.",
  },
};

export default function SiteLayout({
  children,
  navVariant = "solid",
}: SiteLayoutProps) {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });

    const seo = seoMap[location.pathname] || seoMap["/"];

    document.title = seo.title;

    let description = document.querySelector(
      'meta[name="description"]'
    ) as HTMLMetaElement | null;

    if (!description) {
      description = document.createElement("meta");
      description.name = "description";
      document.head.appendChild(description);
    }

    description.content = seo.description;

    let ogTitle = document.querySelector(
      'meta[property="og:title"]'
    ) as HTMLMetaElement | null;

    if (!ogTitle) {
      ogTitle = document.createElement("meta");
      ogTitle.setAttribute("property", "og:title");
      document.head.appendChild(ogTitle);
    }

    ogTitle.content = seo.title;

    let ogDescription = document.querySelector(
      'meta[property="og:description"]'
    ) as HTMLMetaElement | null;

    if (!ogDescription) {
      ogDescription = document.createElement("meta");
      ogDescription.setAttribute("property", "og:description");
      document.head.appendChild(ogDescription);
    }

    ogDescription.content = seo.description;

    let canonical = document.querySelector(
      'link[rel="canonical"]'
    ) as HTMLLinkElement | null;

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }

    canonical.href = `https://servovalve.co.kr${location.pathname}`;
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar variant={navVariant} />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
